<template>
    <div :class="['table-wrap', 'table-id-' + uid]" :ref="uid" >
        <table>
            <thead>
                <tr>
                    <th v-for="column, columnIndex in columns"  
                        :key="column.id"                      
                        v-on:mouseenter="onThHover(column.id, true)"
                        v-on:mouseleave="onThHover(column.id, false)"
                        v-bind:class="[{ 'fixed' : isFixedColumn(column.id) }, {'fixed-last' : isFixedColumnLast(column.id)}, 'column-' + column.id, getMetaAttributesClasses(column)]"
                        v-bind:colspan="getColumnColspan(column)"
                        v-bind:rowspan="getColumnRowspan(column)"
                    >
                        <div class="th-wrapper">
                            <span v-if="column.fixable"
                                v-on:click="fireMetaEvent(column, 'onPinClick', $event)"  
                                v-bind:class="['pin-fixed-column', { 'pin-fixed' : isFixedColumn(column.id) }]"
                            ></span>
                            <span>{{ column.title }}</span>
                            <span
                                v-if="column.sortable"
                                v-on:click="fireMetaEvent(column, 'onSortClick', $event)"  
                                v-bind:class="['sorting', 'sorting-' + getColumnSort(column.id) ]"
                            >
                                <i class="sorting-top"></i>
                                <i class="sorting-bottom"></i>
                            </span>
                        </div>
                        <div 
                            class="column-date-comparison-wrapper" 
                            v-if="column.comparable"
                            v-on:click="fireMetaEvent(column, 'onCompareClick', $event)"  
                        >
                            <i  v-bind:class="{'active' : getColumnColspan(column) > 1}"
                            ></i>
                        </div>
                    </th>                    
                </tr>
                <template v-if="headerRows && headerRows.length > 0">
                    <template v-for="row, rowIndex in headerRows">
                        <Row :row="row" :rowIndex="rowIndex" :namespace="tableNamespace"/>     
                    </template>          
                </template>
            </thead>
            <tbody v-if="bodyRows && bodyRows.length > 0">
                <template v-for="row, rowIndex in bodyRows">
                    <template v-if="isComponent(row)">
                        <component 
                            v-bind:is="importComponent(row)"
                            :namespace="tableNamespace"
                            :row="row"
                            :rowIndex="rowIndex"
                            :key="row.id"
                        />
                    </template>
                    <template v-else>
                        <Row :row="row" :rowIndex="rowIndex" :namespace="tableNamespace" :key="row.id"/>                        
                    </template>                    
                </template>          
            </tbody>
            <tfoot v-if="footerRows && footerRows.length > 0">
                <template v-for="row, rowIndex in footerRows">
                    <Row :row="row" :rowIndex="rowIndex" :namespace="tableNamespace"/>     
                </template>          
            </tfoot>
        </table>  
    </div>
</template>

<style lang="scss" scoped>
    @import "./style.scss";
</style>

<script>
    import _ from 'lodash';
    import Row from './TableRow';
    
    import internalMixin from './mixin/internal.mixin.js';

    export default {
        name: "cc-table",
        components : {
            Row
        },
        mixins : [
            internalMixin
        ],        
        props: {
            namespace: {
                type: String,
                required: false,
                default: () => null
            },

            header: {
                type: Object,
                required: true
            },

            body: {
                type: Object,
                required: true
            },

            footer: {
                type: Object,
                required: false,
                default: () => { return {};}
            }            
        },

        data() {
            return {
                uid: this._uid,
            }
        }, 

        created() {
            this.setTableStoreNamespacePrefix(this.tableNamespace);
            this.initTableStore();
            window.addEventListener('resize', this.updateFixedColumnsLeftOffset);
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.updateFixedColumnsLeftOffset);
        },
        
        mounted() {
            this.updateHeader(this.header); 
            this.updateBody(this.body); 
            this.updateFooter(this.footer);            
        },

        updated() {
            this.updateFixedColumnsLeftOffset();
        },

        computed: {
            tableNamespace: function(){
                return _.isString(this.namespace) ? this.namespace : this.uid.toString();
            },
        },

        watch : {
            header: {
                handler: function(newValue) {this.updateHeader(newValue)},
                deep: true
            },

            body: {
                handler: function(newValue) {this.updateBody(newValue)},
                deep: true
            },

            footer: {
                handler: function(newValue) {this.updateFooter(newValue)},
                deep: true
            },

            fixedColumns: function() {
                this.updateFixedColumnsLeftOffset();
            }, 

            columns: function(){
                requestAnimationFrame(this.updateStickyHeaderTopOffset);
            }
        },

        methods : {

            updateFooter(footer) {
                if( _.isArray(footer.rows) ) {
                    this.tableStoreDispatch('setFooterRows', footer.rows);
                }
            },

            updateBody(body) {
                if( _.isArray(body.rows) ) {
                    this.tableStoreDispatch('setBodyRows', body.rows);
                }
            },

            updateHeader(header) {
                let self = this;
                if( _.isArray(header.columns) ) {                        
                    this.tableStoreDispatch('setColumns', header.columns);
                }

                if( _.isArray(header.fixedColumns) ) {                        
                    this.tableStoreDispatch('setFixedColumns', header.fixedColumns);
                }

                if( _.isObject(header.sortedColumns) && ! _.isNull(header.sortedColumns) ) {                        
                    this.tableStoreDispatch('setSortedColumns', header.sortedColumns);
                }

                if( _.isArray(header.rows) ) {
                    this.tableStoreDispatch('setHeaderRows', header.rows);
                }                
            },

            updateStickyHeaderTopOffset() {
                let height = 0;
                let self = this;
                this.$refs[this.uid].querySelectorAll('thead tr').forEach(function(tr){
                    tr.querySelectorAll('th, td').forEach(e => e.style.top = height + 'px');
                    height += tr.offsetHeight;
                })
            },

            updateFixedColumnsLeftOffset() {
                

                let cssGroup = 'cssStyleFixedColumnTable-' + this.uid;
                document.querySelectorAll('[data-css-group="'+ cssGroup +'"]').forEach(existedStyle => {
                    existedStyle.remove();
                });

                let leftList = {};
                let width    = 0;

                // Load width's of all fixed columns
                let fixedColumnsList = this.$refs[this.uid].querySelectorAll('th.fixed');
                fixedColumnsList.forEach(function(thColumn){ 
                    let columnClass = _.find( thColumn.classList, c => c.match(/^column-/) );                     
                    leftList[columnClass] = width;
                    width += parseInt(thColumn.offsetWidth);                    
                });

                let css = ''; 
                let lastFixedTh = null;
                let lastFixedTd = null;                               
                _.map(_.keys(leftList), columnClass => {
                    let thLeft = leftList[columnClass];
                    lastFixedTh = ' th.' + columnClass;
                    //CSS for th
                    css += '.table-id-' + this.uid + ' th.' + columnClass +' { left: ' + thLeft + 'px; position:sticky; } ';

                    let tdWidth = 0;
                    document.querySelectorAll('tbody tr:first-child td.'  + columnClass).forEach(td => {
                        let tdLeft = tdWidth + thLeft;                       
                        let cellClass = _.find( td.classList, c => c.match(/^cell-/) );                    
                        
                        //CSS for td
                        css += '.table-id-' + this.uid + ' td.' + columnClass + '.' + cellClass + ' { left: ' + tdLeft + 'px; position:sticky; } ';
                        tdWidth += parseInt(td.offsetWidth);

                        lastFixedTd = ' td.' + columnClass + '.' + cellClass;
                    });
                });

                if( lastFixedTh !== null   ) {
                    css += '.table-id-' + this.uid + ' tbody ' + lastFixedTd + `{
                        border-right: 0px !important;
                        z-index: 5;
                    }`;

                    css += '.table-id-' + this.uid + lastFixedTh + `,
                    .table-id-` + this.uid  + ' thead ' + lastFixedTd + `{
                        border-right: 0px !important;
                        z-index: 6;
                    }`;

                    css += '.table-id-' + this.uid + lastFixedTh + `::after,
                    .table-id-` + this.uid + lastFixedTd + `::after
                    {
                        box-shadow: 2px 0px 2px rgba(0,0,0,0.5);
                        content: "";
                        width: 2px;
                        height: 100%;
                        position: absolute;
                        top: 0;
                        right: 0;
                    }
                    .table-id-` + this.uid + lastFixedTh + `::after{
                        box-shadow: 2px 0px 2px rgba(0,0,0,0.2);
                    }`;
                }

                let head = document.querySelector('head');
                    let style = document.createElement('style');

                    head.appendChild(style);

                    style.type = 'text/css';
                    style.dataset['cssGroup'] = cssGroup;
                    if (style.styleSheet){
                        // This is required for IE8 and below.
                        style.styleSheet.cssText = css;
                    } else {
                        style.appendChild(document.createTextNode(css));
                    }
            },

            onThHover(columnId, status) {
                let element = this.$refs[this.uid];
                requestAnimationFrame(() => {

                    let cssGroup       = 'cssStyleHoverColumnTable-' + this.uid;
                    let cssId          = cssGroup + '_column-'+ columnId;
                    let alreadyHovered = false;

                    document.querySelectorAll('[data-css-group="'+ cssGroup +'"]').forEach(existedStyle =>{
                        if(existedStyle.id === cssId) alreadyHovered = true;
                        existedStyle.remove()
                    });

                    if( alreadyHovered ) return;

                    let css  = '.table-id-' + this.uid + ' tbody td.column-' + columnId +' { background-color: #e1e1e1 !important; }';
                        css += '.table-id-' + this.uid + ' tbody tr:nth-child(2n) td.column-' + columnId +' {background-color: #d1d1d1 !important;}';

                    let head = document.querySelector('head');
                    let style = document.createElement('style');

                    head.appendChild(style);

                    style.type = 'text/css';
                    style.id = cssId;
                    style.dataset['cssGroup'] = cssGroup;
                    if (style.styleSheet){
                        // This is required for IE8 and below.
                        style.styleSheet.cssText = css;
                    } else {
                        style.appendChild(document.createTextNode(css));
                    }
                });
            }
        }
    }
</script>


// WEBPACK FOOTER //
// src/components/common/table_v3/Table.vue